import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import './ScheduleGenerationConfigModal.scss';
import TimePicker from 'rc-time-picker';
import 'rc-time-picker/assets/index.css';
import moment from 'moment';

class ScheduleGenerationConfigModal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      currentScreen: 'main', // 'main' or 'hours'
      startDate: moment(),
      endDate: moment().add(6, 'days'),
      sameHoursForAllDays: false,
      minShiftLength: { hours: 4, minutes: 0 },
      maxShiftLength: { hours: 8, minutes: 0 },
      minScheduleHoursPerWeek: 40,
      overtimeThreshold: 40,
      minBreakTime: 480,
      availabilityFulfillLevel: 'best_effort',
      useOpenShifts: true,
      hoursOfOperation: {
        monday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
        tuesday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
        wednesday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
        thursday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
        friday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
        saturday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
        sunday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) }
      }
    };
  }

  componentDidMount() {
    this.loadSavedConfiguration();
  }

  loadSavedConfiguration = () => {
    try {
      const savedConfig = sessionStorage.getItem('scheduleGeneratorConfig');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        // Convert string dates back to moment objects
        const hoursOfOperation = Object.entries(config.hoursOfOperation).reduce((acc, [day, hours]) => {
          acc[day] = {
            start: hours.start ? moment(hours.start) : null,
            end: hours.end ? moment(hours.end) : null
          };
          return acc;
        }, {});
        // Ensure all required fields are present
        this.setState({
          startDate: moment(), // Always use current date
          endDate: config.endDate ? moment(config.endDate) : moment().add(6, 'days'),
          sameHoursForAllDays: config.sameHoursForAllDays || false,
          minShiftLength: config.minShiftLength || { hours: 4, minutes: 0 },
          maxShiftLength: config.maxShiftLength || { hours: 8, minutes: 0 },
          minScheduleHoursPerWeek: config.minScheduleHoursPerWeek || 40,
          overtimeThreshold: config.overtimeThreshold || 40,
          minBreakTime: config.minBreakTime || 480,
          availabilityFulfillLevel: config.availabilityFulfillLevel || 'best_effort',
          useOpenShifts: config.useOpenShifts !== undefined ? config.useOpenShifts : true,
          hoursOfOperation: hoursOfOperation || {
            monday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
            tuesday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
            wednesday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
            thursday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
            friday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
            saturday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) },
            sunday: { start: moment().hour(9).minute(0), end: moment().hour(17).minute(0) }
          }
        });
      }
    } catch (error) {
      console.error('Error loading saved configuration:', error);
    }
  };

  handleHoursButtonClick = () => {
    this.setState({ currentScreen: 'hours' });
  };

  handleBackToMain = () => {
    this.setState({ currentScreen: 'main' });
  };

  handleSameHoursChange = (event) => {
    const checked = event.target.checked;
    this.setState(prevState => {
      const newHours = { ...prevState.hoursOfOperation };
      if (checked) {
        // Copy Monday's hours to all other days, but ensure Monday has valid hours
        if (newHours.monday && newHours.monday.start && newHours.monday.end) {
          Object.keys(newHours).forEach(day => {
            if (day !== 'monday') {
              newHours[day] = { ...newHours.monday };
            }
          });
        } else {
          // If Monday is closed, set all days to default hours
          const defaultHours = {
            start: moment().hour(9).minute(0),
            end: moment().hour(17).minute(0)
          };
          Object.keys(newHours).forEach(day => {
            newHours[day] = { ...defaultHours };
          });
        }
      }
      return {
        sameHoursForAllDays: checked,
        hoursOfOperation: newHours
      };
    });
  };

  handleTimeChange = (day, type, value) => {
    // Prevent null values from being set - if value is null, keep the existing value
    if (!value) {
      return;
    }

    this.setState(prevState => {
      const newHours = { ...prevState.hoursOfOperation };
      newHours[day][type] = value;

      if (prevState.sameHoursForAllDays && day === 'monday') {
        // Copy Monday's new time to all other days, but ensure the new time is valid
        if (value) {
          Object.keys(newHours).forEach(otherDay => {
            if (otherDay !== 'monday') {
              newHours[otherDay][type] = value;
            }
          });
        }
      }

      return { hoursOfOperation: newHours };
    });
  };

  handleDayClosedChange = (day, isClosed) => {
    this.setState(prevState => {
      const newHours = { ...prevState.hoursOfOperation };

      if (isClosed) {
        // Set the day as closed by setting both start and end to null
        newHours[day] = { start: null, end: null };
      } else {
        // Set default hours when reopening a day
        newHours[day] = {
          start: moment().hour(9).minute(0),
          end: moment().hour(17).minute(0)
        };
      }

      if (prevState.sameHoursForAllDays && day === 'monday') {
        // Copy Monday's new hours to all other days, but ensure the new hours are valid
        if (newHours[day] && newHours[day].start && newHours[day].end) {
          Object.keys(newHours).forEach(otherDay => {
            if (otherDay !== 'monday') {
              newHours[otherDay] = { ...newHours[day] };
            }
          });
        }
      }

      return { hoursOfOperation: newHours };
    });
  };

  handleShiftLengthChange = (type, field, value) => {
    this.setState(prevState => ({
      [type]: {
        ...prevState[type],
        [field]: parseInt(value) || 0
      }
    }));
  };

  handleCancel = () => {
    const { cancelModal } = this.props;
    cancelModal();
  };

  handleStartDateChange = (date) => {
    this.setState({ startDate: date });
  };

  handleSave = () => {
    try {
      // Convert moment objects to strings for storage
      const hoursOfOperation = Object.entries(this.state.hoursOfOperation).reduce((acc, [day, hours]) => {
        acc[day] = {
          start: hours.start ? hours.start.toISOString() : null,
          end: hours.end ? hours.end.toISOString() : null
        };
        return acc;
      }, {});
      const configToSave = {
        startDate: this.state.startDate ? this.state.startDate.toISOString() : moment().toISOString(),
        endDate: this.state.endDate ? this.state.endDate.toISOString() : moment().add(6, 'days').toISOString(),
        sameHoursForAllDays: this.state.sameHoursForAllDays,
        minShiftLength: this.state.minShiftLength,
        maxShiftLength: this.state.maxShiftLength,
        minScheduleHoursPerWeek: this.state.minScheduleHoursPerWeek,
        overtimeThreshold: this.state.overtimeThreshold,
        minBreakTime: this.state.minBreakTime,
        availabilityFulfillLevel: this.state.availabilityFulfillLevel,
        useOpenShifts: this.state.useOpenShifts,
        hoursOfOperation
      };
      sessionStorage.setItem('scheduleGeneratorConfig', JSON.stringify(configToSave));
      this.props.cancelModal();
    } catch (error) {
      console.error('Error saving configuration:', error);
    }
  };

  formatHoursSummary = () => {
    const { hoursOfOperation, sameHoursForAllDays } = this.state;
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    if (sameHoursForAllDays) {
      const mondayHours = hoursOfOperation.monday;
      if (!mondayHours.start || !mondayHours.end) {
        return 'Every day: Closed';
      }
      return `Every day ${mondayHours.start.format('h:mmA')}-${mondayHours.end.format('h:mmA')}`;
    }

    const summaries = days.map(day => {
      const hours = hoursOfOperation[day];
      const dayAbbr = day.slice(0, 3).toUpperCase();

      if (!hours.start || !hours.end) {
        return `${dayAbbr} Closed`;
      }

      return `${dayAbbr} ${hours.start.format('h:mmA')}-${hours.end.format('h:mmA')}`;
    });

    // Join with commas and truncate if too long
    let summary = summaries.join(', ');
    if (summary.length > 50) {
      summary = summary.substring(0, 47) + '...';
    }
    return summary;
  };

  renderMainScreen() {
    const {
      minShiftLength,
      maxShiftLength,
      hoursOfOperation,
      minScheduleHoursPerWeek,
      overtimeThreshold,
      minBreakTime,
      availabilityFulfillLevel,
      useOpenShifts,
      startDate,
      endDate
    } = this.state;
    const hasHoursConfig = Object.values(hoursOfOperation).some(hours =>
      hours.start && hours.end && hours.start.isValid() && hours.end.isValid()
    ) || Object.values(hoursOfOperation).some(hours =>
      !hours.start || !hours.end
    );
    return (
      <div className="config-fields-container">
        <div className="title-row">
          <label className="title">Schedule Generator</label>
          <button className="close-button" onClick={this.props.cancelModal}>
            <img src="icons/close-simple.svg" alt="Close" />
          </button>
        </div>

        <div className="config-section">
          <label className="section-label">Date Range</label>
          <div className="field-row">
            <div className="config-field-vertical">
              <label className="field-label">Start</label>
              <input
                type="date"
                value={startDate ? startDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')}
                onChange={(e) => this.handleStartDateChange(moment(e.target.value))}
                className="field-input"
                min={moment().format('YYYY-MM-DD')}
              />
            </div>
            <div className="config-field-vertical">
              <label className="field-label">End</label>
              <input
                type="date"
                value={endDate ? endDate.format('YYYY-MM-DD') : moment().add(6, 'days').format('YYYY-MM-DD')}
                onChange={(e) => this.setState({ endDate: moment(e.target.value) })}
                className="field-input"
                min={startDate ? startDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')}
              />
            </div>
          </div>
        </div>

        <div className="config-section">
          <label className="section-label">Shift Length</label>
          <div className="field-row">
            <div className="shift-length-group">
              <label>Min</label>
              <div className="time-inputs">
                <input
                  type="number"
                  min="0"
                  max="24"
                  value={minShiftLength.hours}
                  onChange={(e) => this.handleShiftLengthChange('minShiftLength', 'hours', e.target.value)}
                  className="time-input"
                />
                <span>hr</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={minShiftLength.minutes}
                  onChange={(e) => this.handleShiftLengthChange('minShiftLength', 'minutes', e.target.value)}
                  className="time-input"
                />
                <span>min</span>
              </div>
            </div>
            <div className="shift-length-group">
              <label>Max</label>
              <div className="time-inputs">
                <input
                  type="number"
                  min="0"
                  max="24"
                  value={maxShiftLength.hours}
                  onChange={(e) => this.handleShiftLengthChange('maxShiftLength', 'hours', e.target.value)}
                  className="time-input"
                />
                <span>hr</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={maxShiftLength.minutes}
                  onChange={(e) => this.handleShiftLengthChange('maxShiftLength', 'minutes', e.target.value)}
                  className="time-input"
                />
                <span>min</span>
              </div>
            </div>
          </div>
        </div>

        <div className="config-section">
          <label className="section-label">Scheduling Rules</label>
          <div className="rules-grid">
            <div className="config-field-vertical">
              <label className="field-label">Min Hours/Week</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  min="0"
                  max="168"
                  value={minScheduleHoursPerWeek}
                  onChange={(e) => this.setState({ minScheduleHoursPerWeek: parseInt(e.target.value) || 0 })}
                  className="field-input"
                />
                <span className="unit">hrs</span>
              </div>
            </div>
            <div className="config-field-vertical">
              <label className="field-label">Overtime Threshold</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  min="0"
                  max="168"
                  value={overtimeThreshold}
                  onChange={(e) => this.setState({ overtimeThreshold: parseInt(e.target.value) || 0 })}
                  className="field-input"
                />
                <span className="unit">hrs</span>
              </div>
            </div>
            <div className="config-field-vertical">
              <label className="field-label">Rest Period</label>
              <div className="input-with-unit">
                <input
                  type="number"
                  min="0"
                  max="24"
                  value={minBreakTime / 60}
                  onChange={(e) => this.setState({ minBreakTime: (parseInt(e.target.value) || 0) * 60 })}
                  className="field-input"
                />
                <span className="unit">hrs</span>
              </div>
            </div>
          </div>
        </div>

        <div className="config-section">
          <label className="section-label">Availability</label>
          <div className="availability-toggle">
            <button
              type="button"
              className={`toggle-option ${availabilityFulfillLevel === 'always' ? 'active' : ''}`}
              onClick={() => this.setState({ availabilityFulfillLevel: 'always' })}
            >
              Always
            </button>
            <button
              type="button"
              className={`toggle-option ${availabilityFulfillLevel === 'best_effort' ? 'active' : ''}`}
              onClick={() => this.setState({ availabilityFulfillLevel: 'best_effort' })}
            >
              Best Effort
            </button>
            <button
              type="button"
              className={`toggle-option ${availabilityFulfillLevel === 'never' ? 'active' : ''}`}
              onClick={() => this.setState({ availabilityFulfillLevel: 'never' })}
            >
              Never
            </button>
          </div>
          <div className="checkbox-field">
            <input
              type="checkbox"
              id="useOpenShifts"
              checked={useOpenShifts}
              onChange={(e) => this.setState({ useOpenShifts: e.target.checked })}
            />
            <label htmlFor="useOpenShifts">
              Use open shifts for unfilled demand
            </label>
          </div>
        </div>

        <div className="config-section">
          <label className="section-label">Hours of Operation</label>
          <button
            className="hours-button"
            onClick={this.handleHoursButtonClick}
          >
            <div className="hours-button-content">
              <span>{hasHoursConfig ? this.formatHoursSummary() : 'Configure hours'}</span>
              <img src="icons/week-picker-right.svg" alt=">" />
            </div>
          </button>
        </div>

        <div className="button-row">
          <button className="cancel-button" onClick={this.props.cancelModal}>Cancel</button>
          <button className="save-button" onClick={this.handleSave}>Save</button>
        </div>
      </div>
    );
  }

  renderHoursScreen() {
    const { sameHoursForAllDays, hoursOfOperation } = this.state;
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    return (
      <>
        <div className="title-row">
          <button className="back-button" onClick={this.handleBackToMain}>
            <img src="icons/week-picker-left.svg" alt="<" />
          </button>
          <label className="title">Hours of Operation</label>
          <button className="close-button" onClick={this.props.cancelModal}>
            <img src="icons/close-simple.svg" alt="Close" />
          </button>
        </div>

        <div className="hours-config-container">
          <div className="same-hours-toggle">
            <input
              type="checkbox"
              id="sameHours"
              checked={sameHoursForAllDays}
              onChange={this.handleSameHoursChange}
            />
            <label htmlFor="sameHours">
              Hours of operation is the same for every day of the week
            </label>
          </div>

          <div className="hours-table">
            <div className="hours-header">
              <div className="day-column">Day</div>
              <div className="closed-column">Closed</div>
              <div className="time-column">Start Time</div>
              <div className="time-column">End Time</div>
            </div>
            {days.map(day => {
              const isDayClosed = !hoursOfOperation[day].start || !hoursOfOperation[day].end;
              return (
                <div
                  key={day}
                  className={`day-row ${sameHoursForAllDays && day !== 'monday' ? 'disabled' : ''}`}
                >
                  <div className="day-column">
                    <label className="day-label">
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </label>
                  </div>
                  <div className="closed-column">
                    <input
                      type="checkbox"
                      id={`closed-${day}`}
                      checked={isDayClosed}
                      onChange={(e) => this.handleDayClosedChange(day, e.target.checked)}
                      disabled={sameHoursForAllDays && day !== 'monday'}
                    />
                  </div>
                  <div className="time-column">
                    <TimePicker
                      value={hoursOfOperation[day].start}
                      onChange={(value) => this.handleTimeChange(day, 'start', value)}
                      format="h:mm A"
                      use12Hours
                      minuteStep={15}
                      disabled={(sameHoursForAllDays && day !== 'monday') || isDayClosed}
                      allowClear={false}
                      clearIcon={null}
                      showClear={false}
                    />
                  </div>
                  <div className="time-column">
                    <TimePicker
                      value={hoursOfOperation[day].end}
                      onChange={(value) => this.handleTimeChange(day, 'end', value)}
                      format="h:mm A"
                      use12Hours
                      minuteStep={15}
                      disabled={(sameHoursForAllDays && day !== 'monday') || isDayClosed}
                      allowClear={false}
                      clearIcon={null}
                      showClear={false}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  render() {
    const { currentScreen } = this.state;

    return (
      <div className="schedule-generation-config-container">
        {currentScreen === 'main' ? (
          <div className="screen main-screen">
            {this.renderMainScreen()}
          </div>
        ) : (
          <div className="screen hours-screen">
            {this.renderHoursScreen()}
          </div>
        )}
      </div>
    );
  }
}

export default injectIntl(ScheduleGenerationConfigModal);
